import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useWallletContext } from "../hook/useWalletContext";
import { validateFields } from "../common/helper";
import * as yup from "yup";
import { BrowserProvider } from "ethers";
import { useWeb3ModalProvider } from "@web3modal/ethers/react";
import { AllowanceService } from "../openapi";
import { REQUEST_TYPE } from "../common/const";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../states/ShowProgress";

type Props = {
  open: boolean;
  handleOpen: any;
  apy: number;
  handleRefresh: any;
};

export const AddAllowanceModal = ({
  open,
  handleOpen,
  apy,
  handleRefresh,
}: Props) => {
  const { walletProvider } = useWeb3ModalProvider();

  const { theme } = useWallletContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [address, setAddress] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setAddress("");
    }
  }, [open]);

  const handleAdd = async () => {
    try {
      await validateFields(
        yup.object().shape({
          address: yup
            .string()
            .required("Please input the address.")
            .matches(
              /^(0x)?[0-9a-fA-F]{40}$/,
              "Please input the correct address."
            ),
        }),
        ["address"],
        [address]
      );
    } catch (err: any) {
      toast.warn(err.message, { theme: theme });
      return;
    }

    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      setShowProgress(true);

      const signedMessage = await signer.signMessage(
        JSON.stringify({
          apy: apy,
          requestType: REQUEST_TYPE.CREATE_ALLOWANCE,
          account: address,
        })
      );

      AllowanceService.createAllowance({
        apy: apy,
        requestType: REQUEST_TYPE.CREATE_ALLOWANCE,
        account: address,
        signedMessage: signedMessage,
      })
        .then((res) => {
          setShowProgress(false);

          if (res === "true") {
            toast.success("All allowance has been successful.", {
              theme: theme,
            });
            handleOpen(false);
            handleRefresh();
          } else {
            toast.error("Allowance is already exist.", { theme: theme });
          }
        })
        .catch((err) => {
          setShowProgress(false);
          toast.error("Add allowance has been failed.", { theme: theme });
        });
    } catch (err) {
      console.log(err);
      toast.error("Adding allowance has been failed.", { theme: theme });
    }
  };

  return (
    <>
      <div className={`hs-overlay ti-modal ${open ? "open" : "hidden"}`}>
        <div className="hs-overlay-open:mt-7 ti-modal-box mt-0 ease-out min-h-[calc(100%-3.5rem)] flex items-center">
          <div className="ti-modal-content">
            <div className="ti-modal-header">
              <h3 className="ti-modal-title"> Add Allowance </h3>
              <button
                type="button"
                className="hs-dropdown-toggle ti-modal-close-btn"
                data-hs-overlay="#hs-vertically-centered-modal"
                onClick={() => handleOpen(false)}
              >
                <span className="sr-only">Close</span>
                <svg
                  className="w-3.5 h-3.5"
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.258206 1.00652C0.351976 0.912791 0.479126 0.860131 0.611706 0.860131C0.744296 0.860131 0.871447 0.912791 0.965207 1.00652L3.61171 3.65302L6.25822 1.00652C6.30432 0.958771 6.35952 0.920671 6.42052 0.894471C6.48152 0.868271 6.54712 0.854471 6.61352 0.853901C6.67992 0.853321 6.74572 0.865971 6.80722 0.891111C6.86862 0.916251 6.92442 0.953381 6.97142 1.00032C7.01832 1.04727 7.05552 1.1031 7.08062 1.16454C7.10572 1.22599 7.11842 1.29183 7.11782 1.35822C7.11722 1.42461 7.10342 1.49022 7.07722 1.55122C7.05102 1.61222 7.01292 1.6674 6.96522 1.71352L4.31871 4.36002L6.96522 7.00648C7.05632 7.10078 7.10672 7.22708 7.10552 7.35818C7.10442 7.48928 7.05182 7.61468 6.95912 7.70738C6.86642 7.80018 6.74102 7.85268 6.60992 7.85388C6.47882 7.85498 6.35252 7.80458 6.25822 7.71348L3.61171 5.06702L0.965207 7.71348C0.870907 7.80458 0.744606 7.85498 0.613506 7.85388C0.482406 7.85268 0.357007 7.80018 0.264297 7.70738C0.171597 7.61468 0.119017 7.48928 0.117877 7.35818C0.116737 7.22708 0.167126 7.10078 0.258206 7.00648L2.90471 4.36002L0.258206 1.71352C0.164476 1.61976 0.111816 1.4926 0.111816 1.36002C0.111816 1.22744 0.164476 1.10028 0.258206 1.00652Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div className="ti-modal-body">
              <p className="text-gray-800 dark:text-white/70">
                Please input the address to allow this user to use staking
                service.
              </p>
              <div className="mt-2 grid grid-cols-12 gap-x-5">
                <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
                  <label
                    htmlFor="inline-input-label-with-helper-text"
                    className="ti-form-label my-auto text-md"
                  >
                    Address:
                  </label>
                </div>
                <div className="col-span-12 md:col-span-6 xxl:col-span-9">
                  <input
                    type="text"
                    className="my-auto ti-form-input "
                    placeholder="Please input the address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="ti-modal-footer">
              <button
                type="button"
                className="hs-dropdown-toggle ti-btn ti-border font-medium bg-white text-gray-700 shadow-sm align-middle hover:bg-gray-50 focus:ring-offset-white focus:ring-primary dark:bg-bgdark dark:hover:bg-black/20 dark:border-white/10 dark:text-white/70 dark:hover:text-white dark:focus:ring-offset-white/10"
                data-hs-overlay="#hs-vertically-centered-modal"
                onClick={() => handleOpen(false)}
              >
                Close
              </button>
              <Link
                className="ti-btn ti-btn-primary"
                to="#"
                onClick={handleAdd}
              >
                Add
              </Link>
            </div>
          </div>
        </div>
      </div>
      {open && (
        <div
          data-hs-overlay-backdrop-template=""
          className="transition duration fixed inset-0 z-50 bg-gray-900 bg-opacity-50 dark:bg-opacity-80 hs-overlay-backdrop"
        ></div>
      )}
    </>
  );
};
